import { useMemo, useState } from 'react'
import {
	getCoreRowModel,
	getExpandedRowModel,
	getPaginationRowModel,
	useReactTable,
	ExpandedState,
	type ColumnDef,
} from '@tanstack/react-table'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'

import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import Spinner from '@/common/components/Spinner'

import type { ProviderBillingBreakdown } from '../../type'

interface ProviderBreakdownTableProps {
	data: ProviderBillingBreakdown[]
	isLoading?: boolean
	footer?: React.ReactNode
}

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50]

const formatCurrency = (value: number): string => {
	return `฿${value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`
}

/** A single renderable row — either a combined provider or one variant */
type DisplayRow = {
	providerName: string
	label: string | null // 'ปรับแตก' | 'ไม่ปรับแตก' | null
	companyRate: number
	transferIn: number
	transferOut: number
	profit: number
	charge: number
	nonPromotionCharge: number
	promotionCharge: number
	promotions: ProviderBillingBreakdown['promotions']
}

/** Flatten providers: those with variants become two rows, others stay one row */
function flattenProviders(providers: ProviderBillingBreakdown[]): DisplayRow[] {
	const rows: DisplayRow[] = []
	for (const p of providers) {
		for (const v of p.variants) {
			rows.push({
				providerName: p.providerName,
				// Only show label when the provider actually has a HARD variant —
				// i.e. more than one variant exists, or the single variant is HARD.
				label:
					p.variants.length > 1 || v.isHard
						? v.isHard
							? 'ปรับแตก'
							: 'ไม่ปรับแตก'
						: null,
				companyRate: v.companyRate,
				transferIn: v.transferIn,
				transferOut: v.transferOut,
				profit: v.profit,
				charge: v.charge,
				nonPromotionCharge: v.nonPromotionCharge,
				promotionCharge: v.promotionCharge,
				promotions: v.promotions,
			})
		}
	}
	return rows
}

// Minimal column defs — TanStack is used only for pagination + expand state.
// All headers and cells are rendered manually.
const DUMMY_COLUMNS: ColumnDef<DisplayRow>[] = Array.from(
	{ length: 9 },
	(_, i) => ({
		id: String(i),
		accessorFn: () => null,
	}),
)
const PROVIDER_COL_COUNT = DUMMY_COLUMNS.length

export default function ProviderBreakdownTable({
	data,
	isLoading,
	footer,
}: ProviderBreakdownTableProps) {
	const [expanded, setExpanded] = useState<ExpandedState>({})

	// Memoize so the array reference is stable across renders — otherwise
	// TanStack's autoReset* sees "new" data every render and loops forever.
	const flatData = useMemo(() => flattenProviders(data), [data])

	const table = useReactTable({
		data: flatData,
		columns: DUMMY_COLUMNS,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getRowCanExpand: (row) => row.original.promotions.length > 0,
		state: { expanded },
		onExpandedChange: setExpanded,
		autoResetPageIndex: false,
		autoResetExpanded: false,
	})

	if (isLoading) {
		return <Spinner />
	}

	return (
		<div className="space-y-4">
			<ScrollArea className="rounded-md border">
				<Table className="relative">
					<TableHeader>
						<TableRow>
							<TableHead className="w-8"></TableHead>
							<TableHead>ผู้ให้บริการ</TableHead>
							<TableHead className="text-right">อัตราคอมพานี</TableHead>
							<TableHead className="text-right">ยอดโอนเข้า</TableHead>
							<TableHead className="text-right">ยอดโอนออก</TableHead>
							<TableHead className="text-right">กินลูกค้า</TableHead>
							<TableHead className="text-right">
								ยอดเรียกเก็บ (ไม่รวมโปรโมชั่น)
							</TableHead>
							<TableHead className="text-right">
								ยอดเรียกเก็บ (โปรโมชั่น)
							</TableHead>
							<TableHead className="text-right">ยอดเรียกเก็บรวม</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => {
								const d = row.original
								return (
									<>
										<TableRow key={row.id}>
											{/* Expander */}
											<TableCell>
												{d.promotions.length > 0 && (
													<button
														onClick={() => row.toggleExpanded()}
														className="flex h-8 w-8 items-center justify-center rounded hover:bg-muted"
													>
														{row.getIsExpanded() ? (
															<ChevronLeftIcon className="h-4 w-4 rotate-90" />
														) : (
															<ChevronRightIcon className="h-4 w-4" />
														)}
													</button>
												)}
											</TableCell>

											{/* Provider name */}
											<TableCell>
												<span className="font-medium">
													{d.providerName}
													{d.label && (
														<span className="ml-1 font-normal text-muted-foreground">
															({d.label})
														</span>
													)}
												</span>
											</TableCell>

											<TableCell className="text-right">
												{(d.companyRate * 100).toFixed(0)}%
											</TableCell>
											<TableCell className="text-right text-green-600">
												{formatCurrency(d.transferIn)}
											</TableCell>
											<TableCell className="text-right text-red-600">
												{formatCurrency(d.transferOut)}
											</TableCell>
											<TableCell className="text-right text-emerald-600">
												{formatCurrency(d.profit)}
											</TableCell>
											<TableCell className="text-right font-semibold text-purple-600">
												{formatCurrency(d.nonPromotionCharge)}
											</TableCell>
											<TableCell className="text-right font-semibold text-orange-600">
												{formatCurrency(d.promotionCharge)}
											</TableCell>
											<TableCell className="text-right font-semibold text-blue-600">
												{formatCurrency(d.charge)}
											</TableCell>
										</TableRow>

										{/* Expanded promotions sub-table */}
										{row.getIsExpanded() && d.promotions.length > 0 && (
											<TableRow key={`${row.id}-expanded`}>
												<TableCell
													colSpan={PROVIDER_COL_COUNT}
													className="bg-muted/50 p-0"
												>
													<div className="p-4">
														<div className="mb-2 flex items-center gap-2">
															<span className="text-sm font-semibold">
																โปรโมชั่นของ {d.providerName}
																{d.label && (
																	<span className="ml-1 text-muted-foreground">
																		({d.label})
																	</span>
																)}
															</span>
															<span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
																{d.promotions.length} รายการ
															</span>
														</div>
														<div className="overflow-x-auto">
															<Table>
																<TableHeader>
																	<TableRow>
																		<TableHead className="w-12"></TableHead>
																		<TableHead>ชื่อโปรโมชั่น</TableHead>
																		<TableHead className="text-right">
																			อัตรา
																		</TableHead>
																		<TableHead className="text-right">
																			ยอดโอนเข้า
																		</TableHead>
																		<TableHead className="text-right">
																			ยอดโอนออก
																		</TableHead>
																		<TableHead className="text-right">
																			กินลูกค้า
																		</TableHead>
																		<TableHead className="text-right">
																			ยอดเรียกเก็บ
																		</TableHead>
																	</TableRow>
																</TableHeader>
																<TableBody>
																	{d.promotions.map((promotion, index) => (
																		<TableRow
																			key={promotion.promotionId || index}
																		>
																			<TableCell></TableCell>
																			<TableCell className="font-medium">
																				{promotion.promotionName ||
																					'ไม่มีชื่อโปรโมชั่น'}
																			</TableCell>
																			<TableCell className="text-right">
																				{promotion.chargeRate !== null
																					? `${(promotion.chargeRate * 100).toFixed(0)}%`
																					: '-'}
																			</TableCell>
																			<TableCell className="text-right text-green-600">
																				{formatCurrency(promotion.transferIn)}
																			</TableCell>
																			<TableCell className="text-right text-red-600">
																				{formatCurrency(promotion.transferOut)}
																			</TableCell>
																			<TableCell className="text-right text-emerald-600">
																				{formatCurrency(promotion.profit)}
																			</TableCell>
																			<TableCell className="text-right font-semibold text-orange-600">
																				{formatCurrency(promotion.charge)}
																			</TableCell>
																		</TableRow>
																	))}
																	<TableRow className="border-t-2">
																		<TableCell></TableCell>
																		<TableCell className="font-bold">
																			รวมโปรโมชั่น
																		</TableCell>
																		<TableCell></TableCell>
																		<TableCell className="text-right font-bold text-green-600">
																			{formatCurrency(
																				d.promotions.reduce(
																					(sum, p) => sum + p.transferIn,
																					0,
																				),
																			)}
																		</TableCell>
																		<TableCell className="text-right font-bold text-red-600">
																			{formatCurrency(
																				d.promotions.reduce(
																					(sum, p) => sum + p.transferOut,
																					0,
																				),
																			)}
																		</TableCell>
																		<TableCell className="text-right font-bold text-emerald-600">
																			{formatCurrency(
																				d.promotions.reduce(
																					(sum, p) => sum + p.profit,
																					0,
																				),
																			)}
																		</TableCell>
																		<TableCell className="text-right font-bold text-orange-600">
																			{formatCurrency(d.promotionCharge)}
																		</TableCell>
																	</TableRow>
																</TableBody>
															</Table>
														</div>
													</div>
												</TableCell>
											</TableRow>
										)}
									</>
								)
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={PROVIDER_COL_COUNT}
									className="h-24 text-center"
								>
									ไม่มีข้อมูล
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{footer && (
						<TableFooter>
							<TableRow>{footer}</TableRow>
						</TableFooter>
					)}
				</Table>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			{/* Pagination Controls */}
			<div className="flex flex-col items-center justify-end gap-2 py-4 sm:flex-row">
				<div className="flex w-full items-center justify-between">
					<div className="flex-1 text-sm text-muted-foreground">
						{flatData.length > 0 ? (
							<>
								แสดง{' '}
								{table.getState().pagination.pageIndex *
									table.getState().pagination.pageSize +
									1}{' '}
								ถึง{' '}
								{Math.min(
									(table.getState().pagination.pageIndex + 1) *
										table.getState().pagination.pageSize,
									flatData.length,
								)}{' '}
								จาก {flatData.length} รายการ
							</>
						) : (
							'ไม่มีรายการ'
						)}
					</div>
					<div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
						<div className="flex items-center space-x-2">
							<p className="whitespace-nowrap text-sm font-medium">
								แถวต่อหน้า
							</p>
							<Select
								value={`${table.getState().pagination.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value))
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue
										placeholder={table.getState().pagination.pageSize}
									/>
								</SelectTrigger>
								<SelectContent side="top">
									{PAGE_SIZE_OPTIONS.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
					<div className="flex w-[150px] items-center justify-center text-sm font-medium">
						{flatData.length > 0 ? (
							<>
								หน้า {table.getState().pagination.pageIndex + 1} จาก{' '}
								{table.getPageCount()}
							</>
						) : (
							'ไม่มีหน้า'
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button
							aria-label="ไปหน้าแรก"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(0)}
							disabled={!table.getCanPreviousPage()}
						>
							<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าก่อนหน้า"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าถัดไป"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าสุดท้าย"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(table.getPageCount() - 1)}
							disabled={!table.getCanNextPage()}
						>
							<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
